import { fetchApi } from './client';
import type { PaginatedResponse, CatalogEntry, SortOrder, VendorSortField } from './types';

interface ListVendorsParams {
    q?: string;
    include_stats?: boolean;
    limit?: number;
    offset?: number;
    /** Opt into the curated-only Company Catalog listing (VI-510). */
    curated?: boolean;
    sort?: VendorSortField;
    order?: SortOrder;
    /** Restrict to these provider abbreviations (e.g. `['SB', 'MANUAL']`). Empty = no filter. */
    providers?: string[];
    /** Restrict to entries with zero linked tenants (`tenant_count = 0`), for purging unused rows. */
    noTenants?: boolean;
    /** Restrict to entries with a finished OSINT report. */
    hasOsintReport?: boolean;
}

/**
 * List catalog entries with optional search and stats.
 */
export async function listVendors(
    params: ListVendorsParams = {},
): Promise<PaginatedResponse<CatalogEntry>> {
    const searchParams = new URLSearchParams();

    if (params.q) {
        searchParams.set('q', params.q);
    }

    if (params.include_stats) {
        searchParams.set('include_stats', 'true');
    }

    if (params.curated) {
        searchParams.set('curated', 'true');
    }

    if (params.limit !== undefined) {
        searchParams.set('limit', String(params.limit));
    }

    if (params.offset !== undefined) {
        searchParams.set('offset', String(params.offset));
    }

    if (params.sort) {
        searchParams.set('sort', params.sort);
    }

    if (params.order) {
        searchParams.set('order', params.order);
    }

    if (params.providers && params.providers.length > 0) {
        searchParams.set('providers', params.providers.join(','));
    }

    if (params.noTenants) {
        searchParams.set('no_tenants', 'true');
    }

    if (params.hasOsintReport) {
        searchParams.set('has_osint_report', 'true');
    }

    const queryString = searchParams.toString();
    const path = `/api/catalog${queryString ? `?${queryString}` : ''}`;

    return fetchApi<PaginatedResponse<CatalogEntry>>(path);
}

/** Distinct providers present in a catalog bucket, backing the provider filter. */
export interface ProviderOptions {
    /** Distinct provider abbreviations actually present (e.g. `['SB', 'MANUAL']`). */
    providers: string[];
    /** True when rows with no provider exist — the UI offers a "No provider" option. */
    hasNoProvider: boolean;
}

/** Distinct providers present in the curated Company Catalog. */
export async function listVendorProviders(): Promise<ProviderOptions> {
    return fetchApi<ProviderOptions>('/api/catalog/providers');
}

/**
 * Get a single catalog entry by its compound `provider:org:slug` id.
 *
 * Kept for SafeBase rows (F4), which carry a full compound id. Company-Catalog
 * rows seeded without provider/org synthesize `::<slug>`, which the backend
 * rejects — use `getVendorByCandidateId` for those / for the detail view.
 */
export async function getVendor(id: string): Promise<CatalogEntry> {
    return fetchApi<CatalogEntry>(`/api/catalog/${encodeURIComponent(id)}?include_stats=true`);
}

/**
 * Get a single catalog entry by its numeric `catalog_product_candidate_id`.
 *
 * The canonical detail lookup (VI-472): the numeric id always resolves,
 * including for rows whose compound id degrades to `::<slug>`. The compound
 * `id` is still used for the sibling CPE/alias/CVE cards, which stay
 * compound-keyed.
 */
export async function getVendorByCandidateId(candidateId: number): Promise<CatalogEntry> {
    return fetchApi<CatalogEntry>(`/api/catalog/by-candidate/${candidateId}?include_stats=true`);
}

/**
 * Full field set for manually adding a Company Catalog entry (admin-only). Only
 * `name` is required; the server mints a MANUAL identity (provider/org) and a
 * `default` slug for any tuple part left blank. Mirrors {@link UpdateVendorParams}
 * so the same form drives both add and edit.
 */
export interface CreateVendorParams {
    name: string;
    provider_abbreviation?: string;
    organization_id?: string;
    domain?: string;
    website_url?: string;
    open_trust_url?: string;
    trust_center_url?: string;
    logo?: string;
    favicon?: string;
    product_name?: string;
    slug?: string;
    is_default?: boolean;
    metadata?: string;
}

/**
 * Manually add a Company Catalog entry (admin-only). Backs the grid "Add vendor"
 * action — the server creates a curated catalog entry from the full field set.
 * When the identity tuple is left blank it either adopts an existing company on
 * a name/domain match (VI-549) or mints a MANUAL identity; a supplied identity
 * is used as-is (upserted onto an existing company that shares it).
 */
export async function createVendor(data: CreateVendorParams): Promise<CatalogEntry> {
    return fetchApi<CatalogEntry>('/api/catalog', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

interface UpdateVendorParams {
    name?: string;
    provider_abbreviation?: string;
    organization_id?: string;
    domain?: string;
    website_url?: string;
    trust_center_url?: string;
    open_trust_url?: string;
    logo?: string;
    favicon?: string;
    product_name?: string;
    slug?: string;
    is_default?: boolean;
    metadata?: string;
}

/**
 * Edit a promoted Company Catalog entry (admin-only), keyed on its numeric
 * `catalog_product_candidate_id` — the same id the detail view and grid selection
 * carry. Updates the company + product + candidate rows server-side.
 */
export async function updateVendor(
    candidateId: number,
    data: UpdateVendorParams,
): Promise<CatalogEntry> {
    return fetchApi<CatalogEntry>(`/api/catalog/by-candidate/${candidateId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
}

/** One deleted company in a bulk-delete outcome — the ids soft-deleted for it. */
export interface DeletedCompany {
    candidate_id: number;
    deleted_company_id: number | null;
    deleted_product_ids: number[];
    deleted_candidate_ids: number[];
}

/** One failed delete. */
export interface FailedDelete {
    candidate_id: number;
    error: string;
}

/**
 * Outcome of a bulk delete: every input id lands in exactly one bucket.
 *
 * - `deleted` – the whole company + its products/candidates were soft-deleted.
 * - `skipped` – ids already gone (a sibling of a company removed earlier in the
 *               same batch — whole-company scope cascades to siblings).
 * - `failed`  – any other error.
 */
export interface BulkDeleteResult {
    deleted: DeletedCompany[];
    skipped: number[];
    failed: FailedDelete[];
}

/**
 * Bulk-delete Company Catalog entries (whole company) by their numeric
 * `catalog_product_candidate_id`. One request; the server processes the ids
 * sequentially so sibling cascades resolve to `skipped` rather than errors.
 */
export async function bulkDeleteCompanies(ids: number[]): Promise<BulkDeleteResult> {
    return fetchApi<BulkDeleteResult>('/api/catalog/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
    });
}
